import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    FaUser, FaGraduationCap, FaUserTie, FaBookOpen,
    FaCreditCard, FaCheckCircle, FaShieldAlt, FaArrowRight,
    FaInfoCircle, FaSpinner, FaSignOutAlt, FaIdCard,
    FaEnvelope, FaPhone, FaBuilding, FaGlobe, FaTimesCircle,
    FaExclamationTriangle, FaReceipt, FaPrint, FaTag, FaKey,
    FaDownload, FaSyncAlt
} from "react-icons/fa";
import {
    getUserPaymentDetails,
    createPaymentOrder,
    verifyPayment,
    syncPaymentStatus,
    logoutUser,
    handleSessionExpired
} from "../../../service/register.service";
import { generateReceiptPdf } from "../../../utils/receiptPdfGenerator";
import ChangePasswordModal from "../../common/ChangePasswordModal";
import styles from "./PaymentPage.module.css";

// -------------------------------------------------------
// 3 Main Delegate Categories
// -------------------------------------------------------
const CATEGORIES = [
    {
        id: "regular",
        title: "Regular Delegate",
        shortTitle: "Regular",
        badge: "Standard",
        icon: <FaUserTie />,
        description: "Full registration for academic, industry & corporate delegates",
    },
    {
        id: "retiree",
        title: "Retiree Delegate",
        shortTitle: "Retiree",
        badge: "Concessional",
        icon: <FaUser />,
        description: "Special reduced rate for senior & retired professionals",
    },
    {
        id: "student",
        title: "Student Delegate",
        shortTitle: "Student",
        badge: "Subsidized",
        icon: <FaGraduationCap />,
        description: "Subsidized rate for verified full-time students & scholars",
    },
];

// -------------------------------------------------------
// Package Catalogue by Category & Nationality (Indian / Foreign)
// Matches PaymentFeeConfig.java
// -------------------------------------------------------
const CATEGORY_PACKAGES = {
    regular: {
        indian: [
            {
                id: "fullaccess",
                label: "Conference only",
                badge: "Regular Pass",
                description: "Full conference access & technical sessions",
                fee: 29500,
                canAddMiniCourse: true,
                iconKey: "fullaccess",
            },
            {
                id: "minicourse",
                label: "Mini-Course Only",
                badge: "Workshop",
                description: "Pre-conference intensive workshop & tutorial session only",
                fee: 5000,
                canAddMiniCourse: false,
                iconKey: "minicourse",
            },
        ],
        foreign: [
            {
                id: "fullaccess",
                label: "Conference only",
                badge: "Regular Pass",
                description: "Full conference access & technical sessions",
                fee: 1050,
                canAddMiniCourse: true,
                iconKey: "fullaccess",
            },
            {
                id: "minicourse",
                label: "Mini-Course Only",
                badge: "Workshop",
                description: "Pre-conference intensive workshop & tutorial session only",
                fee: 300,
                canAddMiniCourse: false,
                iconKey: "minicourse",
            },
            {
                id: "singleday",
                label: "Single-Day Entry Pass",
                badge: "Day Pass",
                description: "Access for any single conference day",
                fee: 425,
                canAddMiniCourse: true,
                iconKey: "singleday",
            },
        ],
    },
    retiree: {
        indian: [
            {
                id: "retiree",
                label: "Conference only for retirees",
                badge: "Retiree Special",
                description: "Full conference access for seniors & retired professionals",
                fee: 10000,
                canAddMiniCourse: true,
                iconKey: "retiree",
            },
            {
                id: "minicourse",
                label: "Mini-Course Only",
                badge: "Workshop",
                description: "Pre-conference intensive workshop & tutorial session only",
                fee: 5000,
                canAddMiniCourse: false,
                iconKey: "minicourse",
            },
        ],
        foreign: [
            {
                id: "retiree",
                label: "Conference only for retirees",
                badge: "Retiree Special",
                description: "Full conference access for seniors & retired professionals",
                fee: 500,
                canAddMiniCourse: true,
                iconKey: "retiree",
            },
            {
                id: "minicourse",
                label: "Mini-Course Only",
                badge: "Workshop",
                description: "Pre-conference intensive workshop & tutorial session only",
                fee: 300,
                canAddMiniCourse: false,
                iconKey: "minicourse",
            },
            {
                id: "singleday",
                label: "Single-Day Entry Pass",
                badge: "Day Pass",
                description: "Access for any single conference day",
                fee: 425,
                canAddMiniCourse: true,
                iconKey: "singleday",
            },
        ],
    },
    student: {
        indian: [
            {
                id: "student",
                label: "Conference only for students",
                badge: "Student Pass",
                description: "Full conference access for verified students & scholars",
                fee: 5000,
                canAddMiniCourse: true,
                iconKey: "student",
            },
            {
                id: "minicourse",
                label: "Mini-Course Only",
                badge: "Workshop",
                description: "Pre-conference intensive workshop & tutorial session only",
                fee: 5000,
                canAddMiniCourse: false,
                iconKey: "minicourse",
            },
        ],
        foreign: [
            {
                id: "student",
                label: "Conference only for students",
                badge: "Student Pass",
                description: "Full conference access for verified students & scholars",
                fee: 250,
                canAddMiniCourse: true,
                iconKey: "student",
            },
            {
                id: "minicourse",
                label: "Mini-Course Only",
                badge: "Workshop",
                description: "Pre-conference intensive workshop & tutorial session only",
                fee: 300,
                canAddMiniCourse: false,
                iconKey: "minicourse",
            },
            {
                id: "singleday",
                label: "Single-Day Entry Pass",
                badge: "Day Pass",
                description: "Access for any single conference day",
                fee: 425,
                canAddMiniCourse: true,
                iconKey: "singleday",
            },
        ],
    },
};

const ADDON_DATA = {
    indian: {
        currency: "INR",
        symbol: "₹",
        fee: 5000,
        label: "Mini-Course (Optional Add-on)",
        description: "Pre-conference intensive tutorial & workshop session",
    },
    foreign: {
        currency: "USD",
        symbol: "$",
        fee: 300,
        label: "Mini-Course (Optional Add-on)",
        description: "Pre-conference intensive tutorial & workshop session",
    },
};

const TIER_ICONS = {
    fullaccess: <FaUserTie />,
    normal: <FaUserTie />,
    regular: <FaUserTie />,
    student: <FaGraduationCap />,
    retiree: <FaUser />,
    minicourse: <FaBookOpen />,
    singleday: <FaIdCard />,
};

const formatAmount = (amount, symbol, isForeign) => {
    if (amount === undefined || amount === null || isNaN(amount)) return "—";
    const formatted = Number(amount).toLocaleString(isForeign ? "en-US" : "en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${symbol} ${formatted}`;
};

const getTierHumanTitle = (tierId) => {
    switch (tierId?.toLowerCase()) {
        case "student":
            return "Conference only for students";
        case "retiree":
            return "Conference only for retirees";
        case "minicourse":
            return "Mini-Course Only";
        case "singleday":
            return "Single-Day Entry Pass";
        case "fullaccess":
        case "normal":
        case "regular":
        default:
            return "Conference only";
    }
};

// Loads the Razorpay checkout script dynamically once.
const loadRazorpayScript = () =>
    new Promise((resolve) => {
        if (document.getElementById("razorpay-script")) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.id = "razorpay-script";
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

// -------------------------------------------------------
// Component
// -------------------------------------------------------
const PaymentPage = () => {
    const navigate = useNavigate();

    // User profile data from backend
    const [userData, setUserData] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Form selection state: 3 radio buttons
    const [selectedCategory, setSelectedCategory] = useState("regular");
    const [selectedTier, setSelectedTier] = useState("fullaccess");
    const [miniCourse, setMiniCourse] = useState(false);
    const [isIeeeMember, setIsIeeeMember] = useState(false);
    const [ieeeMemberNo, setIeeeMemberNo] = useState("");
    const [ieeeError, setIeeeError] = useState("");

    // Payment action loading
    const [loading, setLoading] = useState(false);
    const [changePassOpen, setChangePassOpen] = useState(false);

    // ---- Fetch user details & payment status on mount / refresh ----
    useEffect(() => {
        const token = localStorage.getItem("ivec_token");
        if (!token) {
            navigate("/login");
            return;
        }

        const fetchDetails = async () => {
            try {
                const res = await getUserPaymentDetails();
                if (res?.data) {
                    const data = res.data;
                    setUserData(data);

                    // Pre-fill IEEE member number if already present in user record
                    if (data.ieeeMemberNo) {
                        setIsIeeeMember(true);
                        setIeeeMemberNo(data.ieeeMemberNo);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch user payment details:", err);
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    await handleSessionExpired();
                    return;
                }
            } finally {
                setPageLoading(false);
            }
        };

        fetchDetails();
    }, [navigate, refreshTrigger]);

    // Derive delegate type and appropriate fee catalogue
    const delegateType = userData?.delegateType?.toLowerCase() === "foreign" ? "foreign" : "indian";
    const isForeign = delegateType === "foreign";
    const currency = isForeign ? "USD" : "INR";
    const symbol = isForeign ? "$" : "₹";
    const addonConfig = ADDON_DATA[delegateType];

    // Packages for currently selected category
    const availablePackages = CATEGORY_PACKAGES[selectedCategory]?.[delegateType] || [];

    // Ensure selectedTier is valid for availablePackages
    const tierObj = availablePackages.find((p) => p.id === selectedTier) || availablePackages[0];

    // Handle switching between the 3 category radio buttons (Regular, Retiree, Student)
    const handleCategoryChange = (newCat) => {
        setSelectedCategory(newCat);

        // If user was on a conference pass, adapt to the new category's conference pass
        if (
            selectedTier === "fullaccess" ||
            selectedTier === "normal" ||
            selectedTier === "regular" ||
            selectedTier === "retiree" ||
            selectedTier === "student"
        ) {
            if (newCat === "regular") setSelectedTier("fullaccess");
            else if (newCat === "retiree") setSelectedTier("retiree");
            else if (newCat === "student") setSelectedTier("student");
        }
        // "minicourse" and "singleday" remain selected across categories
    };

    // Reset mini-course add-on if selected package does not allow mini course add-on
    useEffect(() => {
        if (tierObj && !tierObj.canAddMiniCourse) {
            setMiniCourse(false);
        }
    }, [tierObj]);

    // Financial calculations
    const baseAmount = tierObj?.fee ?? 0;
    const canAddMiniCourse = Boolean(tierObj?.canAddMiniCourse);
    const miniCourseAmount = (canAddMiniCourse && miniCourse) ? addonConfig.fee : 0;
    const subtotal = baseAmount + miniCourseAmount;

    // 10% IEEE Discount
    const hasValidIeee = isIeeeMember && ieeeMemberNo.trim().length > 0;
    const discountAmount = hasValidIeee ? Math.round(subtotal * 0.10 * 100) / 100 : 0;
    const totalAmount = Math.max(0, subtotal - discountAmount);

    // Logout handler
    const handleLogout = () => {
        Swal.fire({
            title: "Sign Out",
            text: "Are you sure you want to sign out?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2b53a3",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Sign Out",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await logoutUser();
                } catch (e) { }
                localStorage.removeItem("ivec_token");
                localStorage.removeItem("ivec_user");
                localStorage.removeItem("ivec_role");
                localStorage.removeItem("ivec_display_name");
                localStorage.removeItem("ivec_delegate_type");
                navigate("/login");
            }
        });
    };

    // ---- Initiate Razorpay checkout ----
    const handlePay = async () => {
        if (!tierObj) {
            Swal.fire({
                icon: "warning",
                title: "Select a package",
                text: "Please choose a registration package to continue.",
                confirmButtonColor: "#2b53a3",
            });
            return;
        }

        if (isIeeeMember && !ieeeMemberNo.trim()) {
            setIeeeError("Please enter your IEEE Membership Number to claim the 10% discount.");
            Swal.fire({
                icon: "warning",
                title: "IEEE Number Required",
                text: "Please provide your IEEE membership number to avail the 10% discount.",
                confirmButtonColor: "#2b53a3",
            });
            return;
        }
        setIeeeError("");

        setLoading(true);

        // 1. Load Razorpay script
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
            Swal.fire({
                icon: "error",
                title: "Checkout unavailable",
                text: "Unable to load Razorpay payment gateway. Please check your internet connection.",
                confirmButtonColor: "#2b53a3",
            });
            setLoading(false);
            return;
        }

        // 2. Create order on backend
        let orderData;
        try {
            const res = await createPaymentOrder({
                feeTier: tierObj.id,
                category: selectedCategory,
                miniCourseAdded: canAddMiniCourse && miniCourse,
                ieeeMember: isIeeeMember && Boolean(ieeeMemberNo.trim()),
                ieeeMemberNo: isIeeeMember ? ieeeMemberNo.trim() : null,
            });
            orderData = res.data;
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to create payment order. Please try again.";
            Swal.fire({
                icon: "error",
                title: "Order Failed",
                text: msg,
                confirmButtonColor: "#2b53a3",
            });
            setLoading(false);
            return;
        }

        // 3. Open Razorpay modal
        const options = {
            key: orderData.razorpayKeyId,
            amount: orderData.amountInPaise,
            currency: orderData.currency,
            name: "IVEC 2027",
            description: `${tierObj?.label}${canAddMiniCourse && miniCourse ? " + Mini-Course" : ""}${isIeeeMember ? " (IEEE Member)" : ""}`,
            order_id: orderData.razorpayOrderId,
            prefill: {
                name: userData?.displayName || "",
                email: userData?.email || "",
                contact: userData?.mobile ? `${userData.countryCode || ""}${userData.mobile}` : "",
            },
            theme: { color: "#2b53a3" },

            handler: async (response) => {
                try {
                    await verifyPayment({
                        razorpayOrderId: response.razorpay_order_id,
                        razorpayPaymentId: response.razorpay_payment_id,
                        razorpaySignature: response.razorpay_signature,
                    });

                    await Swal.fire({
                        icon: "success",
                        title: "Payment Confirmed! 🎉",
                        html: `
                            <div style="line-height:1.8;text-align:left">
                                <p>Thank you, <strong>${userData?.displayName || "Delegate"}</strong>.</p>
                                <p style="color:#059669;font-weight:600;margin:6px 0;">
                                    Payment ID: ${response.razorpay_payment_id}
                                </p>
                                <p style="color:#4b5563;font-size:13px;background:#f0fdf4;padding:12px;border-radius:8px;border-left:4px solid #10b981;margin-top:10px;">
                                    📧 A payment receipt and confirmation email have been sent to <strong>${userData?.email}</strong>.
                                </p>
                            </div>`,
                        confirmButtonText: "View Status",
                        confirmButtonColor: "#2b53a3",
                    });

                    // Refresh details from backend
                    setRefreshTrigger((prev) => prev + 1);

                } catch (err) {
                    const msg = err?.response?.data?.message || "Payment verification failed. Please contact support.";
                    Swal.fire({
                        icon: "error",
                        title: "Verification Failed",
                        text: msg,
                        confirmButtonColor: "#c4442c",
                    });
                    setRefreshTrigger((prev) => prev + 1);
                } finally {
                    setLoading(false);
                }
            },

            modal: {
                ondismiss: () => {
                    Swal.fire({
                        icon: "info",
                        title: "Payment Incomplete",
                        text: "You closed the payment popup. You can retry anytime.",
                        confirmButtonColor: "#2b53a3",
                    });
                    setLoading(false);
                    setRefreshTrigger((prev) => prev + 1);
                },
            },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
        setLoading(false);
    };

    const [syncing, setSyncing] = useState(false);

    const handleDownloadReceipt = (paymentRecord) => {
        const targetPayment = paymentRecord || activePayment;
        if (!targetPayment) {
            Swal.fire({
                icon: "warning",
                title: "Receipt Unavailable",
                text: "No confirmed payment record was found to generate a receipt.",
                confirmButtonColor: "#2b53a3",
            });
            return;
        }
        try {
            generateReceiptPdf(userData, targetPayment);
        } catch (err) {
            console.error("PDF generation error:", err);
            Swal.fire({
                icon: "error",
                title: "PDF Error",
                text: "Could not generate PDF receipt. You may use the Print / Save option instead.",
                confirmButtonColor: "#2b53a3",
            });
        }
    };

    const handleSyncPayment = async (orderId) => {
        try {
            setSyncing(true);
            const res = await syncPaymentStatus(orderId);
            if (res?.data?.paymentStatus === "SUCCESS") {
                await Swal.fire({
                    icon: "success",
                    title: "Payment Confirmed! 🎉",
                    text: res.message || "Your payment was verified successfully from Razorpay.",
                    confirmButtonColor: "#2b53a3",
                });
                setRefreshTrigger((prev) => prev + 1);
            } else {
                Swal.fire({
                    icon: "info",
                    title: "Gateway Status",
                    text: res?.message || "No captured payment found in payment gateway for this order yet.",
                    confirmButtonColor: "#2b53a3",
                });
            }
        } catch (err) {
            console.error("Payment sync error:", err);
            Swal.fire({
                icon: "error",
                title: "Sync Failed",
                text: err?.response?.data?.message || "Unable to check payment status with gateway at this moment. Please try again later.",
                confirmButtonColor: "#c4442c",
            });
        } finally {
            setSyncing(false);
        }
    };

    if (pageLoading) {
        return (
            <div className={styles.loadingScreen}>
                <FaSpinner className={styles.spinner} />
                <p>Loading delegate details…</p>
            </div>
        );
    }

    const hasPaid = userData?.hasPaid;
    const activePayment = userData?.activePayment;
    const paymentHistory = userData?.paymentHistory || [];
    const hadFailedAttempt = !hasPaid && paymentHistory.some((p) => p.paymentStatus === "FAILED");

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.glowContainer}>
                <div className={styles.glow1} />
                <div className={styles.glow2} />
            </div>

            {/* ---- Top Navigation Bar with User info & Logout ---- */}
            <header className={styles.topNav}>
                <div className="container d-flex align-items-center justify-content-between">
                    <div className={styles.navBrand} onClick={() => navigate("/")} role="button">
                        <span className={styles.brandTitle}>IVEC 2027</span>
                        <span className={styles.brandSub}>International Vacuum Electronics Conference</span>
                    </div>

                    <div className={styles.navRight}>
                        <div className={styles.userChip}>
                            <div className={styles.userAvatar}>
                                <FaUser />
                            </div>
                            <div className={styles.userMeta}>
                                <span className={styles.userName}>{userData?.displayName || userData?.userName}</span>
                                <span className={styles.userEmail}>{userData?.email}</span>
                            </div>
                            <span className={styles.userTag}>
                                {isForeign ? "🌍 Foreign" : "🇮🇳 Indian"}
                            </span>
                        </div>

                        {localStorage.getItem("ivec_role") === "ROLE_ADMIN" && (
                            <button
                                type="button"
                                className={styles.logoutBtn}
                                style={{ background: "#4f46e5", color: "#fff", borderColor: "#4f46e5" }}
                                onClick={() => navigate("/admin")}
                                title="Admin Dashboard"
                            >
                                <span>Admin Panel</span>
                            </button>
                        )}

                        <button
                            type="button"
                            className={styles.logoutBtn}
                            onClick={() => setChangePassOpen(true)}
                            title="Change Password"
                        >
                            <FaKey />
                            <span className={styles.logoutText}>Change Password</span>
                        </button>

                        <button
                            type="button"
                            className={styles.logoutBtn}
                            onClick={handleLogout}
                            title="Sign Out"
                            id="header-logout-btn"
                        >
                            <FaSignOutAlt />
                            <span className={styles.logoutText}>Sign Out</span>
                        </button>
                    </div>
                </div>
            </header>

            <div className="container py-4 py-lg-5">
                {/* ---- Page header ---- */}
                <div className={styles.pageHeader}>
                    <div className={styles.headerBadge}>
                        <FaCreditCard /> Delegate Payment Portal
                    </div>
                    <h1 className={styles.pageTitle}>Registration &amp; Fee Payment</h1>
                    <p className={styles.pageSubtitle}>
                        Select your registration category, choose your conference package, and complete your payment securely.
                    </p>
                </div>

                {/* ---- User Profile / Basic Details Card ---- */}
                <div className={styles.profileCard}>
                    <div className={styles.profileHeader}>
                        <div className={styles.profileHeaderLeft}>
                            <div className={styles.profileIcon}>
                                <FaIdCard />
                            </div>
                            <div className={styles.profileTitleWrap}>
                                <h2 className={styles.profileTitle}>Registered Delegate Details</h2>
                                <p className={styles.profileSubtitle}>
                                    Information submitted during registration
                                </p>
                            </div>
                        </div>
                        <div className={styles.statusBadgeWrap}>
                            {hasPaid ? (
                                <span className={`${styles.statusBadge} ${styles.badgeSuccess}`}>
                                    <FaCheckCircle /> Payment Confirmed
                                </span>
                            ) : hadFailedAttempt ? (
                                <span className={`${styles.statusBadge} ${styles.badgeFailed}`}>
                                    <FaTimesCircle /> Payment Incomplete / Failed
                                </span>
                            ) : (
                                <span className={`${styles.statusBadge} ${styles.badgePending}`}>
                                    <FaExclamationTriangle /> Payment Pending
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.profileGrid}>
                        <div className={styles.infoCol}>
                            <span className={styles.infoLabel}><FaUser /> Delegate Name</span>
                            <span className={styles.infoValue}>{userData?.displayName || "—"}</span>
                        </div>
                        <div className={styles.infoCol}>
                            <span className={styles.infoLabel}><FaEnvelope /> Username / Email</span>
                            <span className={styles.infoValue}>{userData?.email || userData?.userName}</span>
                        </div>
                        <div className={styles.infoCol}>
                            <span className={styles.infoLabel}><FaGlobe /> Delegate Nationality</span>
                            <span className={styles.infoValue}>
                                {isForeign ? "Foreign Delegate (USD)" : "Indian Delegate (INR)"}
                            </span>
                        </div>
                        <div className={styles.infoCol}>
                            <span className={styles.infoLabel}><FaPhone /> Phone / Contact</span>
                            <span className={styles.infoValue}>
                                {userData?.countryCode ? `${userData.countryCode} ` : ""}{userData?.mobile || "—"}
                            </span>
                        </div>

                        {/* Conditional identification */}
                        {isForeign ? (
                            <>
                                <div className={styles.infoCol}>
                                    <span className={styles.infoLabel}>Passport Number</span>
                                    <span className={styles.infoValue}>{userData?.passportNo || "—"}</span>
                                </div>
                                <div className={styles.infoCol}>
                                    <span className={styles.infoLabel}>Nationality &amp; Country</span>
                                    <span className={styles.infoValue}>
                                        {userData?.nationality || userData?.countryOfBirth || "—"}
                                    </span>
                                </div>
                                <div className={styles.infoCol}>
                                    <span className={styles.infoLabel}><FaBuilding /> Affiliation / Organization</span>
                                    <span className={styles.infoValue}>{userData?.affiliation || "—"}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.infoCol}>
                                    <span className={styles.infoLabel}>Aadhaar Name</span>
                                    <span className={styles.infoValue}>{userData?.aadhaarName || "—"}</span>
                                </div>
                                <div className={styles.infoCol}>
                                    <span className={styles.infoLabel}>Aadhaar Number</span>
                                    <span className={styles.infoValue}>
                                        {userData?.aadhaarNo ? `•••• •••• ${userData.aadhaarNo.slice(-4)}` : "—"}
                                    </span>
                                </div>
                            </>
                        )}

                        {userData?.ieeeMemberNo && (
                            <div className={styles.infoCol}>
                                <span className={styles.infoLabel}><FaTag /> IEEE Membership No</span>
                                <span className={`${styles.infoValue} text-success fw-bold`}>
                                    {userData.ieeeMemberNo} (10% Discount)
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ========================================================
                    CASE 1: USER HAS ALREADY PAID (SUCCESS)
                    Show prominent congratulations card & prevent further payments
                   ======================================================== */}
                {hasPaid ? (
                    <div className={styles.paidSuccessCard}>
                        <div className={styles.successIconWrap}>
                            <FaCheckCircle className={styles.bigCheckIcon} />
                        </div>
                        <h2 className={styles.paidTitle}>Registration Confirmed!</h2>
                        <p className={styles.paidSubtitle}>
                            Thank you, <strong>{userData?.displayName}</strong>. Your payment has been received and verified.
                            Your seat at IVEC 2027 is officially reserved.
                        </p>

                        <div className={styles.receiptDetails}>
                            <div className={styles.receiptRow}>
                                <span>Conference Pass:</span>
                                <strong>
                                    {getTierHumanTitle(activePayment?.feeTier)}
                                    {activePayment?.miniCourseAdded ? " + Mini-Course" : ""}
                                </strong>
                            </div>
                            <div className={styles.receiptRow}>
                                <span>Amount Paid:</span>
                                <strong className={styles.receiptAmount}>
                                    {formatAmount(activePayment?.totalAmount, symbol, isForeign)}
                                </strong>
                            </div>
                            {activePayment?.discountAmount > 0 && (
                                <div className={styles.receiptRow}>
                                    <span>IEEE Discount Applied:</span>
                                    <span className="text-success font-monospace">
                                        - {formatAmount(activePayment.discountAmount, symbol, isForeign)} (10% off)
                                    </span>
                                </div>
                            )}
                            <div className={styles.receiptRow}>
                                <span>Razorpay Payment ID:</span>
                                <span className={styles.codeSnippet}>{activePayment?.razorpayPaymentId || "—"}</span>
                            </div>
                            <div className={styles.receiptRow}>
                                <span>Order Reference:</span>
                                <span className={styles.codeSnippet}>{activePayment?.razorpayOrderId || "—"}</span>
                            </div>
                            <div className={styles.receiptRow}>
                                <span>Verified Date:</span>
                                <span>
                                    {activePayment?.verifiedDate
                                        ? new Date(activePayment.verifiedDate).toLocaleString()
                                        : "—"}
                                </span>
                            </div>
                        </div>

                        <div className={styles.noFurtherPaymentNote}>
                            <FaShieldAlt />
                            <span>
                                <strong>Status: Fully Paid.</strong> No further payment is required for this registration.
                                A formal receipt and delegate voucher have been delivered to your email.
                            </span>
                        </div>

                        <div className={styles.successActions}>
                            <button
                                type="button"
                                id="download-receipt-pdf-btn"
                                className={styles.downloadPdfBtn}
                                onClick={() => handleDownloadReceipt(activePayment)}
                            >
                                <FaDownload /> Download Official Receipt (PDF)
                            </button>
                            <button
                                type="button"
                                className={styles.printBtn}
                                onClick={() => window.print()}
                            >
                                <FaPrint /> Print / Save Confirmation
                            </button>
                            <button
                                type="button"
                                className={styles.homeBtn}
                                onClick={() => navigate("/")}
                            >
                                <FaArrowRight /> Return to Main Page
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ========================================================
                       CASE 2: PAYMENT PENDING OR RETRY AFTER FAILED
                       Three Radio Buttons: Regular, Retiree, Student
                       Dynamic Packages for Indian / Foreign, Mini-Course add-on, IEEE discount
                       ======================================================== */
                    <>
                        {hadFailedAttempt && (
                            <div className={styles.retryAlert}>
                                <FaExclamationTriangle className={styles.retryAlertIcon} />
                                <div style={{ flex: 1 }}>
                                    <strong>Previous payment attempt incomplete or cancelled.</strong>
                                    <p>If money was debited from your bank account but your status is not updated, verify with Razorpay gateway now.</p>
                                    <div style={{ marginTop: "10px" }}>
                                        <button
                                            type="button"
                                            className={styles.syncStatusBtn}
                                            onClick={() => handleSyncPayment()}
                                            disabled={syncing}
                                        >
                                            <FaSyncAlt className={syncing ? styles.spinnerInline : ""} />
                                            {syncing ? "Checking Gateway..." : "Verify / Sync Bank Payment"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="row g-4 justify-content-center">
                            {/* ---- Left: Category & Package selection ---- */}
                            <div className="col-12 col-xl-7">
                                <div className={styles.card}>
                                    <div className={styles.cardHeader}>
                                        <div className={styles.cardIcon}><FaCreditCard /></div>
                                        <div className={styles.cardHeaderTitleWrap}>
                                            <h2 className={styles.cardTitle}>Choose Conference Package</h2>
                                            <p className={styles.cardSubtitle}>
                                                Select your delegate category and preferred pass for IVEC 2027
                                            </p>
                                        </div>
                                    </div>

                                    {/* Currency & delegate indicator */}
                                    <div className={styles.delegateBadge}>
                                        <span>Delegate: <strong>{isForeign ? "🌍 Foreign Delegate" : "🇮🇳 Indian Delegate"}</strong></span>
                                        <span>·</span>
                                        <span>Billing Currency: <strong>{currency} ({symbol})</strong></span>
                                    </div>

                                    {/* ========================================================
                                        IEEE MEMBERSHIP DISCOUNT SECTION (ABOVE DELEGATE CATEGORY)
                                       ======================================================== */}
                                    <div className={styles.ieeeSection}>
                                        <div className={styles.ieeeHeader}>
                                            <FaTag className={styles.ieeeTagIcon} />
                                            <div>
                                                <h4 className={styles.ieeeTitle}>IEEE Membership Discount</h4>
                                                <p className={styles.ieeeSubtitle}>
                                                    Active IEEE Members receive an exclusive 10% discount across all conference passes &amp; packages
                                                </p>
                                            </div>
                                        </div>

                                        <div className={styles.ieeeToggleWrap}>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    id="ieee-member-checkbox"
                                                    checked={isIeeeMember}
                                                    onChange={(e) => {
                                                        setIsIeeeMember(e.target.checked);
                                                        if (!e.target.checked) setIeeeError("");
                                                    }}
                                                    className={styles.realCheckbox}
                                                />
                                                <span className={styles.customCheck}>
                                                    {isIeeeMember && <FaCheckCircle />}
                                                </span>
                                                <span className={styles.checkboxText}>
                                                    I am an active <strong>IEEE Member</strong> (10% Discount applied automatically below)
                                                </span>
                                            </label>
                                        </div>

                                        {isIeeeMember && (
                                            <div className={styles.ieeeInputBox}>
                                                <label htmlFor="ieeeMemberNo" className={styles.inputLabel}>
                                                    IEEE Membership Number <span className={styles.requiredStar}>*</span>
                                                </label>
                                                <div className={`${styles.inputGroup} ${ieeeError ? styles.inputGroupError : ""}`}>
                                                    <span className={styles.inputIcon}><FaIdCard /></span>
                                                    <input
                                                        type="text"
                                                        id="ieeeMemberNo"
                                                        name="ieeeMemberNo"
                                                        placeholder="Enter 8 to 12 digit IEEE Member No"
                                                        value={ieeeMemberNo}
                                                        onChange={(e) => {
                                                            setIeeeMemberNo(e.target.value);
                                                            if (e.target.value.trim()) setIeeeError("");
                                                        }}
                                                        className={styles.textInput}
                                                    />
                                                </div>
                                                {ieeeError && <div className={styles.inputErrorText}>{ieeeError}</div>}
                                                {hasValidIeee && (
                                                    <div className={styles.discountPill}>
                                                        <FaCheckCircle /> 10% IEEE Discount Active: You save{" "}
                                                        <strong>{formatAmount(discountAmount, symbol, isForeign)}</strong>!
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* ========================================================
                                        STEP 1: THREE RADIO BUTTONS (REGULAR, RETIREE, STUDENT)
                                       ======================================================== */}
                                    <div className={styles.stepContainer}>
                                        <div className={styles.stepHeaderWrap}>
                                            <span className={styles.stepBadge}>Step 1</span>
                                            <h3 className={styles.stepTitle}>Select Delegate Category</h3>
                                            <span className={styles.stepSubtitle}>
                                                Choose your category below to view eligible conference passes
                                            </span>
                                        </div>

                                        <div className={styles.categoryRadioGrid} role="radiogroup" aria-label="Delegate Category">
                                            {CATEGORIES.map((cat) => {
                                                const isCatActive = selectedCategory === cat.id;

                                                return (
                                                    <label
                                                        key={cat.id}
                                                        htmlFor={`category-radio-${cat.id}`}
                                                        className={`${styles.categoryRadioCard} ${isCatActive ? styles.categoryRadioCardActive : ""}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            id={`category-radio-${cat.id}`}
                                                            name="delegateCategory"
                                                            value={cat.id}
                                                            checked={isCatActive}
                                                            onChange={() => handleCategoryChange(cat.id)}
                                                            className={styles.realRadio}
                                                        />

                                                        <div className={styles.categoryRadioTop}>
                                                            <div className={styles.categoryRadioIcon}>
                                                                {cat.icon}
                                                            </div>
                                                            <div className={styles.categoryRadioIndicator}>
                                                                {isCatActive && <div className={styles.categoryRadioIndicatorInner} />}
                                                            </div>
                                                        </div>

                                                        <span className={styles.categoryBadgePill}>{cat.badge}</span>
                                                        <span className={styles.categoryRadioName}>{cat.title}</span>
                                                        <span className={styles.categoryRadioDesc}>{cat.description}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* ========================================================
                                        STEP 2: PACKAGES FOR SELECTED CATEGORY
                                       ======================================================== */}
                                    <div className={styles.stepContainer}>
                                        <div className={styles.stepHeaderWrap}>
                                            <span className={styles.stepBadge}>Step 2</span>
                                            <h3 className={styles.stepTitle}>
                                                Select Conference Package ({selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)})
                                            </h3>
                                            <span className={styles.stepSubtitle}>
                                                Packages available for {isForeign ? "Foreign" : "Indian"} delegates in this category
                                            </span>
                                        </div>

                                        <div className={styles.tierList}>
                                            {availablePackages.map((tier) => {
                                                const isActive = tierObj?.id === tier.id;
                                                const tierDiscounted = hasValidIeee ? tier.fee * 0.9 : tier.fee;

                                                return (
                                                    <button
                                                        key={tier.id}
                                                        type="button"
                                                        id={`tier-${tier.id}`}
                                                        className={`${styles.tierCard} ${isActive ? styles.tierActive : ""}`}
                                                        onClick={() => setSelectedTier(tier.id)}
                                                    >
                                                        <div className={styles.tierIcon}>
                                                            {TIER_ICONS[tier.iconKey || tier.id] || <FaUser />}
                                                        </div>
                                                        <div className={styles.tierInfo}>
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                <span className={styles.tierLabel}>{tier.label}</span>
                                                                {tier.badge && (
                                                                    <span className={styles.packageBadge}>{tier.badge}</span>
                                                                )}
                                                                {hasValidIeee && (
                                                                    <span className={styles.ieeeDiscountBadge}>10% OFF</span>
                                                                )}
                                                            </div>
                                                            <span className={styles.tierDesc}>{tier.description}</span>
                                                        </div>

                                                        <div className={styles.tierFeeCol}>
                                                            {hasValidIeee ? (
                                                                <>
                                                                    <span className={styles.tierFeeOld}>
                                                                        {formatAmount(tier.fee, symbol, isForeign)}
                                                                    </span>
                                                                    <span className={styles.tierFee}>
                                                                        {formatAmount(tierDiscounted, symbol, isForeign)}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <span className={styles.tierFee}>
                                                                    {formatAmount(tier.fee, symbol, isForeign)}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {isActive && (
                                                            <FaCheckCircle className={styles.checkIcon} />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* ========================================================
                                        STEP 3: WORKSHOP ADD-ON
                                       ======================================================== */}
                                    <div className={styles.stepContainer}>
                                        <div className={styles.stepHeaderWrap}>
                                            <span className={styles.stepBadge}>Step 3</span>
                                            <h3 className={styles.stepTitle}>Optional Workshop Add-on</h3>
                                            <span className={styles.stepSubtitle}>
                                                Pre-conference intensive workshop &amp; tutorial course
                                            </span>
                                        </div>

                                        {/* Optional Mini-course add-on (if applicable) */}
                                        {canAddMiniCourse ? (
                                            <div className={styles.miniCourseSection} style={{ borderTop: "none", paddingTop: 0 }}>
                                                <button
                                                    type="button"
                                                    id="mini-course-toggle"
                                                    className={`${styles.miniCourseCard} ${miniCourse ? styles.miniCourseActive : ""}`}
                                                    onClick={() => setMiniCourse((v) => !v)}
                                                >
                                                    <div className={styles.miniCourseCheckbox}>
                                                        {miniCourse && <FaCheckCircle />}
                                                    </div>
                                                    <div className={styles.miniCourseInfo}>
                                                        <span className={styles.miniCourseLabel}>
                                                            {addonConfig.label}
                                                        </span>
                                                        <span className={styles.miniCourseDesc}>
                                                            {addonConfig.description}
                                                        </span>
                                                    </div>
                                                    <div className={styles.miniCourseFee}>
                                                        + {formatAmount(addonConfig.fee, symbol, isForeign)}
                                                    </div>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className={styles.miniCourseIncludedNotice}>
                                                <FaInfoCircle />
                                                <span>
                                                    Pre-conference Mini-Course is already included as your primary registration package.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ---- Right: Payment Breakdown & Pay Now ---- */}
                            <div className="col-12 col-xl-5">
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryHeader}>
                                        <div className={styles.summaryIcon}><FaCreditCard /></div>
                                        <div className={styles.cardHeaderTitleWrap}>
                                            <h2 className={styles.cardTitle}>Payment Summary</h2>
                                            <p className={styles.cardSubtitle}>Itemized conference billing</p>
                                        </div>
                                    </div>

                                    {/* Line items */}
                                    <div className={styles.summaryItems}>
                                        <div className={styles.summaryRow}>
                                            <span>Delegate Category</span>
                                            <span className={styles.categorySummaryChip}>
                                                {CATEGORIES.find((c) => c.id === selectedCategory)?.title || "Regular"}
                                            </span>
                                        </div>

                                        <div className={styles.summaryRow}>
                                            <span>Package ({tierObj?.label})</span>
                                            <span>{formatAmount(baseAmount, symbol, isForeign)}</span>
                                        </div>

                                        {canAddMiniCourse && miniCourse && (
                                            <div className={styles.summaryRow}>
                                                <span>Optional Mini-Course</span>
                                                <span>{formatAmount(miniCourseAmount, symbol, isForeign)}</span>
                                            </div>
                                        )}

                                        <div className={styles.summaryRow}>
                                            <span className="text-muted">Subtotal</span>
                                            <span className="text-muted">{formatAmount(subtotal, symbol, isForeign)}</span>
                                        </div>

                                        {hasValidIeee && (
                                            <div className={`${styles.summaryRow} ${styles.discountRow}`}>
                                                <span className="d-flex align-items-center gap-1 text-success">
                                                    <FaTag /> IEEE Member 10% Discount
                                                </span>
                                                <span className="text-success fw-bold">
                                                    - {formatAmount(discountAmount, symbol, isForeign)}
                                                </span>
                                            </div>
                                        )}

                                        <div className={styles.summaryDivider} />

                                        <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                                            <span>Total Payable</span>
                                            <span className={styles.totalValue}>
                                                {formatAmount(totalAmount, symbol, isForeign)}
                                            </span>
                                        </div>

                                        <div className={styles.currencyNote}>
                                            <FaInfoCircle />
                                            Amount payable in <strong>{currency}</strong>.
                                            Processed via Razorpay 256-bit secure gateway.
                                        </div>
                                    </div>

                                    {/* Pay button */}
                                    <button
                                        type="button"
                                        id="pay-now-btn"
                                        className={styles.payBtn}
                                        disabled={!tierObj || loading}
                                        onClick={handlePay}
                                    >
                                        {loading ? (
                                            <><FaSpinner className={styles.spinnerInline} /> Initiating Payment…</>
                                        ) : (
                                            <>Proceed to Pay {formatAmount(totalAmount, symbol, isForeign)} <FaArrowRight /></>
                                        )}
                                    </button>

                                    {/* Security seal */}
                                    <div className={styles.securityBadge}>
                                        <FaShieldAlt />
                                        <span>Secured by Razorpay · 256-bit SSL encryption</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ========================================================
                    PAYMENT HISTORY & TRANSACTION LOG TABLE
                    Shown below for both paid and pending/failed delegates
                   ======================================================== */}
                <div className={styles.historySection}>
                    <div className={styles.historyCard}>
                        <div className={styles.historyHeader}>
                            <div className={styles.historyIcon}>
                                <FaReceipt />
                            </div>
                            <div className={styles.cardHeaderTitleWrap}>
                                <h3 className={styles.historyTitle}>Payment History &amp; Transactions</h3>
                                <p className={styles.historySubtitle}>
                                    All payment attempts and receipts recorded for your account
                                </p>
                            </div>
                        </div>

                        {paymentHistory.length === 0 ? (
                            <div className={styles.emptyHistory}>
                                <FaReceipt className={styles.emptyIcon} />
                                <p className={styles.emptyTitle}>No transaction history yet</p>
                                <span className={styles.emptySubtitle}>
                                    Once you attempt a payment, the transaction details will appear here.
                                </span>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className={styles.historyTable}>
                                    <thead>
                                        <tr>
                                            <th>Date &amp; Time</th>
                                            <th>Order ID</th>
                                            <th>Payment ID</th>
                                            <th>Package Tier</th>
                                            <th>Amount</th>
                                            <th>IEEE Discount</th>
                                            <th>Status</th>
                                            <th>Receipt / Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentHistory.map((item, idx) => {
                                            const statusClass =
                                                item.paymentStatus === "SUCCESS"
                                                    ? styles.statusPillSuccess
                                                    : item.paymentStatus === "FAILED"
                                                        ? styles.statusPillFailed
                                                        : styles.statusPillPending;

                                            return (
                                                <tr key={item.paymentId || idx}>
                                                    <td>
                                                        {item.createdDate
                                                            ? new Date(item.createdDate).toLocaleString()
                                                            : "—"}
                                                    </td>
                                                    <td>
                                                        <span className={styles.tableCode}>
                                                            {item.razorpayOrderId || "—"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={styles.tableCode}>
                                                            {item.razorpayPaymentId || "—"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <strong>{getTierHumanTitle(item.feeTier)}</strong>
                                                        {item.miniCourseAdded && (
                                                            <span className={styles.badgeMiniCourse}>+ MiniCourse</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <strong>
                                                            {item.currency} {Number(item.totalAmount).toLocaleString()}
                                                        </strong>
                                                    </td>
                                                    <td>
                                                        {item.discountAmount > 0 ? (
                                                            <span className="text-success fw-bold">
                                                                - {item.currency} {Number(item.discountAmount).toLocaleString()}
                                                            </span>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`${styles.statusPill} ${statusClass}`}>
                                                            {item.paymentStatus}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.paymentStatus === "SUCCESS" ? (
                                                            <button
                                                                type="button"
                                                                className={styles.miniReceiptBtn}
                                                                onClick={() => handleDownloadReceipt(item)}
                                                                title="Download Official PDF Receipt"
                                                            >
                                                                <FaDownload /> Receipt
                                                            </button>
                                                        ) : item.paymentStatus === "CREATED" ? (
                                                            <button
                                                                type="button"
                                                                className={styles.miniSyncBtn}
                                                                onClick={() => handleSyncPayment(item.razorpayOrderId)}
                                                                disabled={syncing}
                                                                title="Check if payment completed with bank/gateway"
                                                            >
                                                                <FaSyncAlt className={syncing ? styles.spinnerInline : ""} /> Sync
                                                            </button>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ChangePasswordModal
                isOpen={changePassOpen}
                onClose={() => setChangePassOpen(false)}
            />
        </div>
    );
};

export default PaymentPage;
